import React, { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  Breadcrumbs as MuiBreadcrumbs,
  Typography,
  Paper as MuiPaper,
  CardContent,
  Box,
  Chip,
} from "@mui/material";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { DataGrid } from "@mui/x-data-grid";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";

import EditDelete from "../../../../components/EditDelete";
import Toolbar from "../../../../components/Toolbar";
import { useDialog } from "../../../../contexts/DialogContext";
import { getTimeStamp } from "../../../../utils/common";
import useWindowDimensions from "../../../../utils/windowDimensions";
import AdvanceFilter from "../../userlist/components/AdvanceFilter";
import useUserActivity from "../../../../hooks/useUserActivity";
import useMainContentDimensions from "../../../../utils/mainContentDimensions";
import BulkUser from "../../user_details/BulkRenewal";
import BulkChangeValidity from "../../userlist/components/BulkChangeValidity";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);

const breadCrump = () => {
  return (
    <Breadcrumbs aria-label="Breadcrumb" mt={2}>
      <Typography>Masters</Typography>
      <Typography>Active Users</Typography>
      <Typography>List</Typography>
    </Breadcrumbs>
  );
};

const userStatus = (status) => {
  if (status == "active") {
    return <Chip label="Active" color="primary" />;
  } else if (status == "online") {
    return <Chip label="Online" color="success" />;
  } else if (status == "expired") {
    return <Chip label="Expired" color="warning" />;
  } else if (status == "offline") {
    return <Chip label="Offline" sx={{ backgroundColor: "grey.500", color: "white" }} />;
  } else if (status == "suspended") {
    return <Chip label="Suspended" sx={{ backgroundColor: "rgb(211, 47, 47)", color: "#fff" }} />;
  } else if (status == "on_hold") {
    return <Chip label="On Hold" sx={{ backgroundColor: "rgb(255, 222, 33)" }} />;
  }
};

const CustomDataGrid = (props) => {
  const { isLoadingDownload, downloadFileType } = useUserActivity();
  const { width: mainContentWidth } = useMainContentDimensions();

  const { height, width } = useWindowDimensions();

  const {
    editPage,
    addPage,
    trans,
    rows,
    rowCount,
    payload,
    setPayload,
    isLoading,
    id,
    handleSortModelChange,
    sortModel,
    downloadFile,
    openDownloadDialog,
    refreshPage,
    setFilterResetFunc,
    paginationModel,
    paginationMeta,
    paging,
    onPaginationModelChange,
    selectedRows,
    setSelectedRows,
    permissionKeys = [],
  } = props;
  console.log(rowCount, "loading child component");

  const { openDialog } = useDialog();

  const columns = [
    {
      field: "userName",
      headerName: "User Name",
      renderCell: (params) => <Link to={"/user/details?id=" + params.id}>{params.value}</Link>,
      width: 200,
    },
    {
      field: "userStatus",
      headerName: "Status",
      renderCell: (params) => {
        return userStatus(params.row["userStatus"]);
      },
      width: 200,
    },
    {
      field: "connectionStatus",
      headerName: "Connection Status",
      renderCell: (params) => {
        return userStatus(params.row["connectionStatus"]);
      },
      width: 200,
    },
    {
      field: "fullName",
      headerName: "Full Name",
      width: 200,
    },
    {
      field: "emailId",
      headerName: "Email Id",
      width: 200,
    },
    {
      field: "mobileNo",
      headerName: "Mobile No",
      width: 130,
    },
    {
      field: "tenantName",
      headerName: "Franchise Name",
      width: 130,
    },
  ];

  const openFilterDialog = () => {
    return openDialog(
      "advance_filter",
      "Advance Filter",
      <AdvanceFilter
        payload={payload}
        setPayload={setPayload}
        setFilterResetFunc={setFilterResetFunc}
      />,
    );
  };

  const bulkUser = () => {
    return openDialog(
      "bulk_renewal",
      "Renewal",
      <BulkUser selectedRows={selectedRows} refreshPage={refreshPage} />,
    );
  };

  const bulkChangeValidity = () => {
    return openDialog(
      "bulk_change_validity",
      "Bulk Change Validity",
      <BulkChangeValidity selectedRows={selectedRows} refreshPage={refreshPage} />,
    );
  };

  return (
    <Card sx={{ width: "100%", maxWidth: mainContentWidth - 64 }}>
      <CardHeader
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" }, // Stack on small screens
          justifyContent: "space-between",
          alignItems: "flex-start", // Adjust alignment as needed
        }}
        action={
          <Toolbar
            addPage={addPage}
            downloadFile={downloadFile}
            openDownloadDialog={openDownloadDialog}
            refreshPage={refreshPage}
            isLoadingDownload={isLoadingDownload}
            downloadFileType={downloadFileType}
            openFilterDialog={openFilterDialog}
            bulkUser={bulkUser}
            bulkChangeValidity={
              permissionKeys.includes("change_validity") ? bulkChangeValidity : null
            }
          />
        }
        title={
          <Typography variant="h6" sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
            {trans("active_users")}
          </Typography>
        }
        subheader={<Box sx={{ display: "flex", flexWrap: "wrap" }}>{breadCrump()}</Box>}
      />
      <CardContent sx={{ height: height - 180, display: "flex", flexDirection: "column" }}>
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <DataGrid
            checkboxSelection
            initialState={{
              pagination: { paginationModel: { pageSize: 100 } },
            }}
            rowCount={rowCount}
            getRowId={(row) => row[id]}
            columns={columns}
            rows={rows}
            page={paging.pageNumber}
            pageSize={paging.pageSize}
            paginationMeta={paginationMeta}
            loading={isLoading}
            pageSizeOptions={[25, 50, 100]}
            paginationMode="server"
            onPaginationModelChange={onPaginationModelChange}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            onRowSelectionModelChange={(newRowSelectionModel) => {
              setSelectedRows(newRowSelectionModel);
            }}
            paginationModel={paginationModel}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
            }
            sx={{
              "& .MuiDataGrid-row.even-row": {
                backgroundColor: "#f9f9f9", // Light gray for even rows
              },
              "& .MuiDataGrid-row.odd-row": {
                backgroundColor: "#ffffff", // White for odd rows
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};
CustomDataGrid.propTypes = {
  editPage: PropTypes.func.isRequired,
  addPage: PropTypes.func.isRequired,
  trans: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  rowCount: PropTypes.number.isRequired,
  paging: PropTypes.object.isRequired,
  onPageChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
  handleSortModelChange: PropTypes.func.isRequired,
  sortModel: PropTypes.array.isRequired,
  permissionKeys: PropTypes.array,
  selectedRows: PropTypes.array,
  setSelectedRows: PropTypes.func,
};
export default CustomDataGrid;
